import { DownloadIcon } from '@heroicons/react/outline';
import { DocumentDownloadIcon } from '@heroicons/react/solid';
import React from 'react';
import { UseQueryResult, useMutation } from 'react-query';
import { toast } from 'react-toastify';
import { match } from 'ts-pattern';
import { downloadEventReimbursementsCSV } from '../../../api/reimbursement';
import PrimaryButton from '../../../components/Buttons/PrimaryButton';
import { useAuth } from '../../../contexts/Auth';
import { StrapiResponseList } from '../../../interfaces/commons';
import { Reimbursement } from '../../../interfaces/reimbursment';
import TableHeader from '../../../components/Table/TableHeader';
import SpeakerReimbursementRow from './SpeakerReimbursementRow';
import { downloadReimbursementsPDFZip } from './reimbursementPdfExport';
import useReimbursementSelection from './useReimbursementSelection';

interface SpeakerReimbursementsTableProps {
  reimbursementsQuery: UseQueryResult<
    StrapiResponseList<Reimbursement>,
    unknown
  >;
  searchingWords?: string[];
  searchParams: URLSearchParams;
}

const SpeakerReimbursementsTable: React.FC<SpeakerReimbursementsTableProps> = ({
  reimbursementsQuery,
  searchingWords,
  searchParams,
}) => {
  const [{ token }] = useAuth();
  const reimbursements = reimbursementsQuery.data?.data || [];
  const {
    allCurrentPageSelected,
    selectedReimbursementIds,
    selectedReimbursements,
    toggleCurrentPage,
    toggleReimbursement,
  } = useReimbursementSelection(reimbursements, searchParams);
  const { mutate: downloadCSV, isLoading: isDownloading } = useMutation(
    'downloadEventReimbursementsCSV',
    () => downloadEventReimbursementsCSV(searchParams, token),
    {
      onError: () => {
        toast.error('Ooops... Qualcosa è andato storto.');
      },
      onSuccess: () => {
        toast.success('Documento CSV scaricato con successo');
      },
    },
  );
  const { mutate: downloadPDFs, isLoading: isDownloadingPDFs } = useMutation(
    'downloadEventReimbursementsPDFs',
    () =>
      downloadReimbursementsPDFZip({
        reimbursements: selectedReimbursements,
        type: 'speaker',
        zipFileName: 'rimborsi-relatori-pdf.zip',
      }),
    {
      onError: () => {
        toast.error('Errore durante la generazione dei PDF');
      },
      onSuccess: () => {
        toast.success('PDF scaricati con successo');
      },
    },
  );

  const tableElements = [
    {
      key: null,
      label: 'Profilo',
      class: 'sticky left-0 w-[200px] bg-gray-50',
    },
    { key: 'submittedAt', label: 'Data invio' },
    { key: null, label: 'Data evento' },
    { key: null, label: 'Sezione' },
    { key: null, label: 'Evento' },
    { key: null, label: 'Compenso lordo' },
    { key: null, label: 'Rimborso lordo' },
    { key: 'paymentDate', label: 'Data pagamento' },
    { key: null, label: 'Stato' },
    { key: null, label: 'Allegati' },
    { key: null, label: 'Nota' },
    { key: null, label: 'Modulo' },
    { key: null, label: 'Id' },
  ];

  return (
    <>
      <div className='flex justify-end gap-3 w-full'>
        <PrimaryButton
          textSmall
          disabled={selectedReimbursements.length === 0 || isDownloadingPDFs}
          isLoading={isDownloadingPDFs}
          onClick={() => downloadPDFs()}
        >
          <span className='text-sm underline flex flex-row'>
            <DocumentDownloadIcon className='w-4 h-4 mt-[3px]' />
            <p>Scarica PDF</p>
          </span>
        </PrimaryButton>
        <PrimaryButton
          textSmall
          disabled={isDownloading}
          isLoading={isDownloading}
          onClick={() => downloadCSV()}
        >
          <span className='text-sm underline flex flex-row'>
            <DownloadIcon className='w-4 h-4 mt-[3px]' /> <p>Scarica CSV</p>
          </span>
        </PrimaryButton>
      </div>

      {match(reimbursementsQuery)
        .with({ status: 'success' }, ({ data: reimbursementsResponse }) => {
          return (
            <div className='-my-2 overflow-x-auto'>
              <div className='inline-block min-w-full py-2 align-middle'>
                <div className='ring-1 border ring-black ring-opacity-5'>
                  <table className='min-w-full table-fixed divide-y divide-gray-300'>
                    <TableHeader
                      columnsNames={tableElements}
                      checked={allCurrentPageSelected}
                      onCheckAll={(event) =>
                        toggleCurrentPage(event.target.checked)
                      }
                    />
                    <tbody className='divide-y divide-gray-200 bg-white'>
                      {reimbursementsResponse?.data?.map((reimbursement) => (
                        <SpeakerReimbursementRow
                          key={reimbursement.id}
                          reimbursement={reimbursement}
                          searchWords={searchingWords || []}
                          selected={selectedReimbursementIds.has(
                            reimbursement.id
                          )}
                          onSelect={(selected) =>
                            toggleReimbursement(reimbursement, selected)
                          }
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          );
        })
        .with({ status: 'error' }, () => {
          return <p>Errore caricamento rimborsi...</p>;
        })
        .with({ status: 'loading' }, { status: 'idle' }, () => {
          return <p>Caricamento rimborsi...</p>;
        })
        .exhaustive()}
    </>
  );
};

export default SpeakerReimbursementsTable;
